import { EMergeValue } from '../types/enums/merge-value';
import { EFieldSource } from '../../../api/fields/types/field-source';
import { EMergeType } from '../merge-field/types/enums/merge-type';
import { FieldPlacementHelper } from './field-placement.helper';
import { ERecipientRole } from '@/dealroom/recipients/types/enums/recipient-role';
import { EnumDocumentType } from '@/enums/DocumentType';
import { EAlignFieldType } from '@/dealroom/fields/context-menu/types/enums/align-field-type';
import { EFieldMenuActionType } from '@/dealroom/fields/context-menu/types/enums/field-menu-action-type';
import { IFieldMenuAction } from '@/dealroom/fields/context-menu/types/field-menu-action';
import { defaultSizes } from '@/dealroom/fields/helpers/field-default.helper';
import { EFieldType } from '@/dealroom/fields/types/enums/field-type';
import { IField } from '@/dealroom/fields/types/field';
import { IFieldPicker } from '@/dealroom/fields/types/field-picker';
import { IFieldPosition } from '@/dealroom/fields/types/field-position';
import { translate } from '@/helpers/translation.helper';
import { IDocumentPage } from '@/types/document-page';
import { IDocument } from '@/types/Document';
import { IEntity } from '@/types/Entity';
import { MergeFieldHelper } from '@/dealroom/fields/merge-field/helpers/merge-field.helper';

export class FieldHelper {
  static mappedFields(field: IField, document: IDocument, entity: IEntity) {
    switch (field.type) {
      case EFieldType.Merge:
        return MergeFieldHelper.mapMergeField(field, document, entity);
      case EFieldType.Text:
      case EFieldType.Formatted:
        return MergeFieldHelper.mapTextField(field, document, entity);
      default:
        return field;
    }
  }
  static getPagesWithFields(pages: IDocumentPage[], fields: IField[]): IDocumentPage[] {
    return pages.map((page: IDocumentPage) =>
      fields.some((field: IField) => field.pageId === page.id)
        ? { ...page, fields: fields.filter((field: IField) => field.pageId === page.id) }
        : page
    );
  }
  static getCommonFieldActions(isEditable: boolean): IFieldMenuAction[] {
    if (!isEditable) {
      return [
        {
          name: 'recipients',
          type: EFieldMenuActionType.Recipients,
          icon: 'connect-field',
        },
      ];
    }
    return [
      {
        name: 'settings',
        type: EFieldMenuActionType.Settings,
        icon: 'settings',
      },
      {
        name: 'recipients',
        type: EFieldMenuActionType.Recipients,
        icon: 'connect-field',
      },
      {
        name: 'align-fields',
        type: EFieldMenuActionType.Align,
        icon: 'align-vertical-center',
      },
      {
        name: 'duplicate',
        type: EFieldMenuActionType.Duplicate,
        icon: 'duplicate',
      },
      {
        name: 'move-field-to-other-page',
        type: EFieldMenuActionType.Move,
        icon: 'move-to-page',
      },
      {
        name: 'text-size',
        type: EFieldMenuActionType.TextSize,
        icon: 'text-size-icon',
      },
      {
        name: 'Delete',
        type: EFieldMenuActionType.Delete,
        icon: 'delete',
      },
    ];
  }

  static getPageRatio(pageWidth: number): number {
    return pageWidth / 900;
  }

  static sortByPosition(fields: IField[], pages: IDocumentPage[]): IField[] {
    if (!fields?.length || !pages?.length) {
      return [];
    }
    const fieldMap: { field: IField; pageNum: number }[] = fields.map((field: IField) => {
      const page = pages.find((documentPage: IDocumentPage) => documentPage.id === field.pageId);
      if (!page) {
        return;
      }
      return { field, pageNum: page.pageNum };
    });
    const fieldMapSorted = fieldMap ? fieldMap.concat().sort(this.sortFieldComparison) : fieldMap;
    return fieldMapSorted.map(({ field }: { field: IField; pageNum: number }) => field);
  }

  private static sortFieldComparison(
    fieldMap1: { field: IField; pageNum: number },
    fieldMap2: { field: IField; pageNum: number }
  ): number {
    if (fieldMap1.pageNum - fieldMap2.pageNum !== 0) {
      return fieldMap1.pageNum - fieldMap2.pageNum;
    }
    if (fieldMap1.field.position.top < fieldMap2.field.position.top) {
      return -1;
    }
    if (
      fieldMap1.field.position.top === fieldMap2.field.position.top &&
      fieldMap1.field.position.left <= fieldMap2.field.position.left
    ) {
      return -1;
    }
    return 1;
  }

  static sortByPositionWithoutPages(originalFields: IField[]): IField[] {
    const fields = [...originalFields];
    return fields.sort((field1: IField, field2: IField) => {
      if (field1.position.top < field2.position.top) {
        return -1;
      }
      if (
        field1.position.top === field2.position.top &&
        field1.position.left <= field2.position.left
      ) {
        return -1;
      }
      return 1;
    });
  }

  static getNextTabField(fields: IField[], selectedIds: string[], pages: IDocumentPage[]): IField {
    const sortedFields: IField[] = this.sortByPosition(fields, pages);
    const selectedField: IField = sortedFields.find((field: IField) => field.id === selectedIds[0]);
    if (!selectedField) {
      return fields[0];
    }
    const index: number = sortedFields.findIndex((field: IField) => {
      return field.id === selectedField.id;
    });
    const nextIndex: number = this.getCyclicIndex(index + 1, fields.length);
    return sortedFields[nextIndex];
  }

  private static getCyclicIndex(index: number, arrayLength: number): number {
    return index % arrayLength;
  }

  static getDuplicateFieldPosition(field: IField): IFieldPosition {
    const space = 10;
    return {
      top: field.position.top + field.position.height + space,
      left: field.position.left + space,
      height: field.position.height,
      width: field.position.width,
    };
  }

  static getFieldContainerPositionBottom(fieldPosition: IFieldPosition): number {
    return fieldPosition.top + fieldPosition.height;
  }

  static getFieldContainerPositionRight(fieldPosition: IFieldPosition): number {
    return fieldPosition.left + fieldPosition.width;
  }

  static getAlignedFields(
    fields: IField[],
    actionType: EAlignFieldType,
    fieldContainerPosition: IFieldPosition
  ): IField[] {
    return fields.map((field: IField) => {
      switch (actionType) {
        case EAlignFieldType.Top: {
          const { top } = fieldContainerPosition;
          if (field.position.top !== top) {
            field.position.top = top;
          }
          break;
        }
        case EAlignFieldType.Right: {
          const right: number = this.getFieldContainerPositionRight(fieldContainerPosition);
          const left: number = right - field.position.width;
          if (field.position.left !== left) {
            field.position.left = left;
          }
          break;
        }
        case EAlignFieldType.Left: {
          const { left } = fieldContainerPosition;
          if (field.position.left !== left) {
            field.position.left = left;
          }
          break;
        }
        case EAlignFieldType.Bottom: {
          const bottom: number = this.getFieldContainerPositionBottom(fieldContainerPosition);
          if (field.position.top + field.position.height !== bottom) {
            field.position.top = bottom - field.position.height;
          }
          break;
        }
        case EAlignFieldType.SameWidth: {
          if (field.type === EFieldType.Check) {
            break;
          }
          const { width } = fields[0].position;
          if (field.position.width !== width) {
            field.position.width = width;
          }
          break;
        }
        default:
          break;
      }
      return field;
    });
  }

  static getTextSizes(): number[] {
    return [8, 9, 10, 11, 12, 13, 14, 18, 24, 30, 36, 48, 60, 72, 96];
  }

  static getDisplayFieldHeight(size: number): number {
    return Math.round(size * 0.6);
  }

  static getSaveFieldHeight(size: number): number {
    return Math.round(size / 0.6);
  }

  static getTextSize(
    fieldPosition: IFieldPosition,
    pageWidth: number,
    textSizes: number[]
  ): number {
    const convertedFieldPosition: IFieldPosition = FieldPlacementHelper.calculateFieldPositionForSave(
      fieldPosition,
      pageWidth
    );
    const textSize: number = this.getDisplayFieldHeight(convertedFieldPosition.height);
    return textSizes.includes(textSize) ? textSize : 0;
  }

  static getRelativeFieldSize(
    fieldType: EFieldType,
    pageWidth: number
  ): { width: number; height: number } {
    const size: {
      width: number;
      height: number;
    } = defaultSizes(fieldType);
    const ratio: number = this.getPageRatio(pageWidth);
    return {
      width: size.width * ratio,
      height: size.height * ratio,
    };
  }

  static getDragImageOffset(
    fieldType: EFieldType,
    pageWidth: number
  ): { left: number; top: number } {
    const relativeSize: { width: number; height: number } = this.getRelativeFieldSize(
      fieldType,
      pageWidth
    );

    return {
      left: relativeSize.width / 2,
      top: relativeSize.height,
    };
  }

  static getSafeFieldLabel(field: IField): string {
    if (field.label) {
      return field.label;
    }
    let fieldLabel = 'field';
    switch (field.type) {
      case EFieldType.Check:
        fieldLabel = 'checkbox-field';
        break;
      case EFieldType.Dropdown:
        fieldLabel = 'dropdown-field';
        break;
      case EFieldType.Formatted:
        fieldLabel = 'formatted-field';
        break;
      case EFieldType.Initials:
        fieldLabel = 'initials-field';
        break;
      case EFieldType.Link:
        fieldLabel = 'link-area';
        break;
      case EFieldType.Merge:
        fieldLabel = 'merge-field';
        break;
      case EFieldType.Signature:
        fieldLabel = 'signature-field';
        break;
      case EFieldType.Text:
        fieldLabel = 'text-field';
        break;
    }
    return translate(fieldLabel);
  }

  static disableField(
    documentType: EnumDocumentType,
    picker: IFieldPicker,
    recipientRole: ERecipientRole
  ): boolean {
    if (
      documentType === EnumDocumentType.Introduction ||
      documentType === EnumDocumentType.Presentation
    ) {
      return this.evaluateFieldForPresentation(picker);
    }
    if ([ERecipientRole.CC, ERecipientRole.Approver].includes(recipientRole)) {
      return this.evaluateFieldForRole(picker);
    }
    return false;
  }

  private static evaluateFieldForPresentation(picker: IFieldPicker): boolean {
    switch (picker.type) {
      case EFieldType.Signature:
      case EFieldType.Initials:
        return true;
      case EFieldType.Merge:
        return picker.mergeValue === EMergeValue.SignDate;
      default:
        return false;
    }
  }

  private static evaluateFieldForRole(picker: IFieldPicker): boolean {
    switch (picker.type) {
      case EFieldType.Signature:
      case EFieldType.Initials:
        return true;
      default:
        return false;
    }
  }

  static getValidatedErrorMessage(type: EFieldType): string {
    switch (type) {
      case EFieldType.Formatted:
        return 'validation-is-required-for-this-field';
      case EFieldType.Link:
        return 'please-enter-a-link';
      case EFieldType.Dropdown:
        return 'a-minimum-of-one-option-is-required';
      default:
        return '';
    }
  }
  static getRequiredErrorMessage(type: EFieldType): string {
    switch (type) {
      case EFieldType.Dropdown:
        return 'you-need-to-select-an-option';
      case EFieldType.Text:
      case EFieldType.Formatted:
        return 'you-need-to-enter-text';
      default:
        return '';
    }
  }

  static getValidatedInput(field: IField): boolean {
    switch (field.type) {
      case EFieldType.Formatted:
        return field.validationRegex ? true : false;
      case EFieldType.Link:
        return field.value ? true : false;
      case EFieldType.Dropdown:
        return field.options && field.options.length > 0 ? true : false;
      default: {
        return true;
      }
    }
  }

  static getSourceClass(source: EFieldSource): string {
    switch (source) {
      case EFieldSource.Sender:
        return 'sender-field';
      case EFieldSource.Recipient:
        return 'recipient-field';
      case EFieldSource.Document:
        return 'document-field';
      default:
        return '';
    }
  }

  static defaultHeight(fieldType: EFieldType): number {
    return defaultSizes(fieldType).height;
  }

  static getTempId(): string {
    return Math.random().toString(36).substring(8);
  }

  static isCollectable = (
    recipientId: string,
    fieldType: EFieldType,
    fieldValue: string,
    connectFirstRecipient = false
  ): boolean => {
    if (!recipientId && !connectFirstRecipient) {
      return false;
    }
    if (
      fieldType === EFieldType.Merge &&
      (fieldValue === EMergeType.SignDateStandard ||
        fieldValue === EMergeType.SignDateShort ||
        fieldValue === EMergeType.SignDateLong)
    ) {
      return true;
    }
    return [EFieldType.Text, EFieldType.Formatted, EFieldType.Check, EFieldType.Dropdown].includes(
      fieldType
    );
  };

  static getSignatureFields(fields: IField[]): IField[] {
    return fields.filter((field: IField) =>
      [EFieldType.Initials, EFieldType.Signature].includes(field.type)
    );
  }

  static getUpdatedDocumentBySignatureField(field: IField) {
    const payload: Partial<IDocument> = {
      isSigning: 1,
    };
    if (field.source === EFieldSource.Sender) {
      payload.isSelfsign = 1;
    }
    if (field.type === EFieldType.Signature) {
      payload.isSigningBiometric = 1;
    } else if (field.type === EFieldType.Initials) {
      payload.isSigningInitials = 1;
    }
    return payload;
  }
}
